import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Alert, Space, Statistic, Row, Col, Tag, Empty, message, Typography, Dropdown, DatePicker, Select, Segmented } from 'antd';
import { CloudDownloadOutlined, DownOutlined, ReloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import api from '../api';
import { exportCsv } from '../utils/exportCsv';
import { useI18n } from '../i18n';

// 金额显示：成交量保留 2 位，token 数量保留 4 位
const fmt = (v, digits = 4) =>
  v == null ? '-' : Number(v).toLocaleString('en-US', { maximumFractionDigits: digits });

// PancakeSwap 成交面板：交易对上所有参与方（含外部地址）的成交存档 + 参与方统计
// renderParty 复用链上记录的地址渲染（钱包名 > 地址簿 > 已知合约 > 截断地址）；
// nameOf 为对应的纯文本版（下拉选项用）
const PairSwapPanel = ({ renderParty, nameOf = (a) => a }) => {
  const { t, ts } = useI18n();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  // 成交明细的筛选条件（本地过滤，数据已全量在内存）
  const [filterRange, setFilterRange] = useState(null);
  const [filterTrader, setFilterTrader] = useState(undefined);
  const [filterDirection, setFilterDirection] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get('/pair-swaps'),
        api.get('/pair-swaps/stats')
      ]);
      setList(listRes.data.data.list);
      setTotal(listRes.data.data.total);
      setStats(statsRes.data.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // latest = RPC 增量查新区块（快）；full = OKLink 发现历史交易 + 链上收据解析（慢，可回溯全部历史）
  const handleSync = async (mode) => {
    setSyncing(true);
    try {
      const res = await api.post('/pair-swaps/sync', { mode });
      const { fetched, inserted, scannedTx, total } = res.data.data;
      message.success(
        mode === 'full'
          ? t('全量回溯完成：解析 {scannedTx} 笔新交易，入库成交 {fetched} 条，新增 {inserted} 条，库内共 {total} 条', { scannedTx, fetched, inserted, total })
          : t('增量同步完成：抓取 {fetched} 条，新增 {inserted} 条，库内共 {total} 条', { fetched, inserted, total })
      );
      load();
    } catch (err) {
      message.error(t('同步失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setSyncing(false);
    }
  };

  // 交易者下拉选项：按成交笔数倒序，显示名称与笔数（支持按名称或地址搜索）
  const traderOptions = useMemo(() => {
    const counts = new Map();
    for (const s of list) counts.set(s.trader, (counts.get(s.trader) || 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([addr, c]) => ({ value: addr, label: `${nameOf(addr)}${t('（{c} 笔）', { c })}` }));
  }, [list, nameOf, t]);

  // 按日期范围（含当天）/交易者/方向过滤成交明细
  const filteredList = useMemo(
    () =>
      list.filter((s) => {
        if (filterDirection !== 'all' && s.direction !== filterDirection) return false;
        if (filterTrader && s.trader.toLowerCase() !== filterTrader.toLowerCase()) return false;
        if (filterRange?.[0] && s.timestamp < filterRange[0].startOf('day').valueOf()) return false;
        if (filterRange?.[1] && s.timestamp > filterRange[1].endOf('day').valueOf()) return false;
        return true;
      }),
    [list, filterRange, filterTrader, filterDirection]
  );

  // 导出参与方统计（当前统计全量）
  const handleExportParticipants = () => {
    exportCsv({
      filename: 'PancakeSwap参与方统计',
      columns: [
        { title: '参与方名称', value: (p) => p.label || '' },
        { title: '地址', value: (p) => p.address },
        { title: '身份', value: (p) => (p.isInternal ? '内部' : '外部') },
        { title: '成交笔数', value: (p) => p.swapCount },
        { title: '买入笔数', value: (p) => p.buyCount },
        { title: '卖出笔数', value: (p) => p.sellCount },
        { title: '买入USGNAA', value: (p) => p.buyUsgnaa },
        { title: '卖出USGNAA', value: (p) => p.sellUsgnaa },
        { title: '买入USDT', value: (p) => p.buyUsdt },
        { title: '卖出USDT', value: (p) => p.sellUsdt },
        { title: 'USDT成交量', value: (p) => p.usdtVolume },
        { title: '当前USDT', value: (p) => p.usdtBalance ?? '未查询' },
        { title: '当前USGNAA', value: (p) => p.usgnaaBalance ?? '未查询' },
        { title: '最近成交时间', value: (p) => new Date(p.lastTime).toLocaleString() }
      ],
      rows: stats?.participants || []
    });
  };

  // 导出成交明细（导出当前筛选结果）
  const handleExportSwaps = () => {
    exportCsv({
      filename: 'PancakeSwap成交明细',
      columns: [
        { title: '时间', value: (s) => new Date(s.timestamp).toLocaleString() },
        { title: '方向', value: (s) => (s.direction === 'buy' ? '买入USGNAA' : '卖出USGNAA') },
        { title: 'USDT', value: (s) => s.usdtAmount },
        { title: 'USGNAA', value: (s) => s.usgnaaAmount },
        { title: '单价(USDT)', value: (s) => (s.price == null ? '' : s.price) },
        { title: '交易者名称', value: (s) => nameOf(s.trader) },
        { title: '交易者地址', value: (s) => s.trader },
        { title: '区块', value: (s) => s.blockNumber },
        { title: '交易哈希', value: (s) => s.txHash }
      ],
      rows: filteredList
    });
  };

  const participantColumns = [
    { title: t('参与方'), dataIndex: 'address', render: (a) => renderParty(a) },
    {
      title: t('身份'),
      dataIndex: 'isInternal',
      width: 80,
      render: (v) => (v ? <Tag color="blue">{t('内部')}</Tag> : <Tag>{t('外部')}</Tag>)
    },
    {
      title: t('成交笔数'),
      dataIndex: 'swapCount',
      align: 'right',
      width: 160,
      render: (v, r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {v}
          <Typography.Text type="secondary">{t('（买{b}/卖{s}）', { b: r.buyCount, s: r.sellCount })}</Typography.Text>
        </span>
      )
    },
    { title: t('买入 USGNAA'), dataIndex: 'buyUsgnaa', align: 'right', render: (v) => fmt(v) },
    { title: t('卖出 USGNAA'), dataIndex: 'sellUsgnaa', align: 'right', render: (v) => fmt(v) },
    { title: t('USDT 成交量'), dataIndex: 'usdtVolume', align: 'right', render: (v) => fmt(v, 2) },
    {
      title: t('当前 USDT'),
      dataIndex: 'usdtBalance',
      align: 'right',
      render: (v) => (v == null ? <Typography.Text type="secondary">{t('未查询')}</Typography.Text> : fmt(v))
    },
    {
      title: t('当前 USGNAA'),
      dataIndex: 'usgnaaBalance',
      align: 'right',
      render: (v) => (v == null ? <Typography.Text type="secondary">{t('未查询')}</Typography.Text> : fmt(v))
    }
  ];

  const swapColumns = [
    {
      title: t('时间'),
      dataIndex: 'timestamp',
      width: 170,
      render: (v) => new Date(v).toLocaleString()
    },
    {
      title: t('方向'),
      dataIndex: 'direction',
      width: 100,
      render: (d) =>
        d === 'buy' ? <Tag color="green">{t('买入 USGNAA_tag')}</Tag> : <Tag color="red">{t('卖出 USGNAA_tag')}</Tag>
    },
    { title: 'USDT', dataIndex: 'usdtAmount', align: 'right', width: 120, render: (v) => fmt(v) },
    { title: 'USGNAA', dataIndex: 'usgnaaAmount', align: 'right', width: 120, render: (v) => fmt(v) },
    {
      title: t('单价 (USDT)'),
      dataIndex: 'price',
      align: 'right',
      width: 110,
      render: (v) => (v == null ? '-' : Number(v).toFixed(6))
    },
    { title: t('交易者'), dataIndex: 'trader', render: (a) => renderParty(a) },
    {
      title: t('交易'),
      dataIndex: 'txHash',
      render: (hash, r) => (
        <a href={r.bscscanUrl} target="_blank" rel="noreferrer">
          {hash.slice(0, 12)}...
        </a>
      )
    }
  ];

  const s = stats;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Dropdown.Button
          type="primary"
          icon={<DownOutlined />}
          loading={syncing}
          onClick={() => handleSync('latest')}
          menu={{
            items: [
              { key: 'full', icon: <CloudDownloadOutlined />, label: t('全量回溯（BscTrace 发现历史交易 + 链上收据解析，较慢）') }
            ],
            onClick: () => handleSync('full')
          }}
        >
          {t('同步成交')}
        </Dropdown.Button>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
          {t('刷新')}
        </Button>
        <Typography.Text type="secondary">{t('库内共 {n} 条成交', { n: total })}</Typography.Text>
      </Space>
      <Alert
        type="info"
        showIcon
        message={t('成交解析自 USDT/USGNAA 交易对合约的链上 Swap 事件（含外部地址参与的交易）；「同步成交」增量抓新区块，下拉「全量回溯」可补齐历史；已随 swap 操作与每小时定时任务自动入库，统计基于本地存档')}
      />
      {error && <Alert type="error" showIcon message={ts(error)} />}

      {s && (
        <>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title={t('总成交笔数')}
                value={s.totalSwaps}
                suffix={<Typography.Text type="secondary" style={{ fontSize: 13 }}>{t('买{b} / 卖{s}', { b: s.buyCount, s: s.sellCount })}</Typography.Text>}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('参与地址')}
                value={s.internal.traderCount + s.external.traderCount}
                suffix={
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    {t('内部{i} / 外部{e}', { i: s.internal.traderCount, e: s.external.traderCount })}
                  </Typography.Text>
                }
              />
            </Col>
            <Col span={6}>
              <Statistic title={t('USDT 总成交量')} value={fmt(s.totalUsdtVolume, 2)} />
            </Col>
            <Col span={6}>
              <Statistic title={t('USGNAA 总成交量')} value={fmt(s.totalUsgnaaVolume, 2)} />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Alert
                type="success"
                message={t('内部参与钱包当前持有：USDT {u} · USGNAA {g}（{n} 个钱包，成交 {v} USDT）', { u: fmt(s.internal.usdtHolding, 2), g: fmt(s.internal.usgnaaHolding, 2), n: s.internal.traderCount, v: fmt(s.internal.usdtVolume, 2) })}
              />
            </Col>
            <Col span={12}>
              <Alert
                type="warning"
                message={t('外部参与地址当前持有：USDT {u} · USGNAA {g}（已查询 {q}/{n} 个地址，成交 {v} USDT）', { u: fmt(s.external.usdtHolding, 2), g: fmt(s.external.usgnaaHolding, 2), q: s.external.holdingQueried, n: s.external.traderCount, v: fmt(s.external.usdtVolume, 2) })}
              />
            </Col>
          </Row>
          <Table
            rowKey="address"
            title={() => (
              <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography.Text strong>{t('参与方统计（按 USDT 成交量倒序）')}</Typography.Text>
                <Button
                  size="small"
                  icon={<FileExcelOutlined />}
                  onClick={handleExportParticipants}
                  disabled={!s.participants.length}
                >
                  {t('导出 Excel')}
                </Button>
              </Space>
            )}
            columns={participantColumns}
            dataSource={s.participants}
            loading={loading}
            scroll={{ x: 'max-content' }}
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
            locale={{ emptyText: <Empty description={t('暂无成交，请先同步')} /> }}
          />
        </>
      )}

      <Table
        rowKey="swapId"
        title={() => (
          <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
            <Typography.Text strong>
              成交明细（{filteredList.length === list.length ? `全部 ${list.length}` : `筛出 ${filteredList.length} / ${list.length}`} 条）
            </Typography.Text>
            <Space wrap>
              <DatePicker.RangePicker
                value={filterRange}
                onChange={setFilterRange}
                allowClear
                placeholder={['开始日期', '结束日期']}
              />
              <Select
                style={{ minWidth: 220 }}
                placeholder={t('交易者（全部）')}
                value={filterTrader}
                onChange={setFilterTrader}
                options={traderOptions}
                allowClear
                showSearch
                filterOption={(input, opt) =>
                  opt.label.toLowerCase().includes(input.toLowerCase()) ||
                  opt.value.toLowerCase().includes(input.toLowerCase())
                }
              />
              <Segmented
                value={filterDirection}
                onChange={setFilterDirection}
                options={[
                  { label: '全部', value: 'all' },
                  { label: '买入', value: 'buy' },
                  { label: '卖出', value: 'sell' }
                ]}
              />
              <Button
                size="small"
                icon={<FileExcelOutlined />}
                onClick={handleExportSwaps}
                disabled={!filteredList.length}
              >
                导出 Excel
              </Button>
            </Space>
          </Space>
        )}
        columns={swapColumns}
        dataSource={filteredList}
        loading={loading}
        scroll={{ x: 'max-content' }}
        size="middle"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        locale={{ emptyText: <Empty description={t('暂无成交，请先同步')} /> }}
      />
    </Space>
  );
};

export default PairSwapPanel;
